import React, { useMemo, useState } from 'react';
import { Button, Form, Icon, Input } from 'antd';
import { validateInputUrls } from '../../utils';
import { useFetchWebsiteIndexEntriesQuery } from '../service';
import { useClusterAndAddServiceableUrlsMutation } from '../../serviceableUrls/service';
import useGetSettingsForPlan from '../../../sales/hooks/useGetPlanSettings';

const { TextArea } = Input;

type Props = {
    websiteGuid: string;
    onFinish: () => void;
    loading?: boolean;
};

export default function AddUrlsManuallyStep({ websiteGuid, onFinish, loading }: Props) {
    const [urlsInput, setUrlsInput] = useState<string>('');
    const [urlsValidationError, setUrlsValidationError] = useState<string>('');

    const { dpMaxServiceableUrls: maximumServiceableUrls } = useGetSettingsForPlan();

    const [addServiceableUrls, { isLoading: isAddingServiceableUrls }] = useClusterAndAddServiceableUrlsMutation();
    const { data: websiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();

    const website = useMemo(() => websiteIndexEntries?.find((websiteIndexEntry) => websiteIndexEntry.guid === websiteGuid), [websiteGuid, websiteIndexEntries]);

    const handleIndividualUrlsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setUrlsValidationError('');
        setUrlsInput(event.target.value);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (website) {
            const [inputUrls, urlsValidationErrors] = validateInputUrls(urlsInput, website.domain);

            if (urlsValidationErrors.some((error) => error) || inputUrls.length === 0) {
                setUrlsValidationError(`Please add valid URLs from domain: ${website.domain}`);
                return;
            }

            if (maximumServiceableUrls && inputUrls.length > maximumServiceableUrls) {
                setUrlsValidationError('URLs limit exceeded');
                return;
            }

            const uniqueUrls = inputUrls.filter((url, index) => inputUrls.indexOf(url) === index);

            try {
                const addedUrls = await addServiceableUrls({ w_guid: websiteGuid, urls: uniqueUrls }).unwrap();

                if (addedUrls.length === 0) {
                    setUrlsValidationError('Something went wrong! Please try again or contact support.');
                    return;
                }

                onFinish();
            } catch (error) {
                setUrlsValidationError('Something went wrong! Please try again or contact support.');
            }
        }
    };

    const isContinueButtonLoading = loading || isAddingServiceableUrls;

    return (
        <Form onSubmit={handleSubmit}>
            <div className="deep-analysis-card">
                <div className="deep-analysis-card-icon sitemap-fail">
                    <Icon type="warning" />
                </div>
                <h2>We couldn't identify any sitemaps for:</h2>
                <div style={{ fontSize: '16px', color: 'rgba(0,0,0,0.45)', marginBottom: '12px' }}>{website?.domain}</div>

                <div className="deep-analysis-card-input-group sitemap-fail">
                    <hr />

                    <label htmlFor="textarea">
                        <div>Add most important URLs</div>
                        <div className="input-description">Maximum {maximumServiceableUrls} URLs, comma separated or one URL per line.</div>
                    </label>

                    <Form.Item
                        validateStatus={urlsValidationError ? 'error' : 'validating'}
                        help={urlsValidationError ? urlsValidationError : ''}
                    >
                        <TextArea
                            value={urlsInput}
                            onChange={handleIndividualUrlsChange}
                            rows={4}
                            id="textarea"
                            placeholder="https://yourdomain.com/home"
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        loading={isContinueButtonLoading}
                        disabled={!website}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </Form>
    );
}
